import { create } from "zustand";
import { atelierApi } from "@services/api/atelier";
import {
  WorkshopSpecialty,
  WorkshopStatusConfig,
  WorkshopWorker,
  WorkshopJob,
  WorkCharge,
  CarWindow,
} from "../types/garage/atelier";
import { useDataStore } from "./user_org";
import type { User } from "../types/user";

/** Roles that map to workshop workers */
const WORKSHOP_ROLE_TO_SPECIALTY: Record<string, WorkshopSpecialty> = {
  mecanicien: "mecanique",
  carrossier: "carrosserie",
  chef_atelier: "autre",
};

/** Convert an org member with a workshop role to a WorkshopWorker */
function memberToWorker(member: User): WorkshopWorker | null {
  const specialty = WORKSHOP_ROLE_TO_SPECIALTY[member.role];
  if (!specialty) return null;
  return {
    id: member.id,
    name: member.infos?.display_name || member.infos?.full_name || "?",
    specialty,
  };
}

export const JOB_STATUSES: WorkshopStatusConfig[] = [
  { id: "en_cours", label: "En cours", color: "#6366F1", bgColor: "#E0E6FF", borderColor: "#CCD0FF" },
  { id: "en_attente", label: "En attente d'accord", color: "#7C7FE0", bgColor: "#C5CBFF", borderColor: "#B3BAFF" },
  { id: "fin_travaux", label: "Fin de travaux", color: "#8B8FD4", bgColor: "#B3BAFF", borderColor: "#A1A8FF" },
  { id: "disponible", label: "Disponible", color: "#8258FF", bgColor: "#DDD2FF", borderColor: "#CDBDFF" },
  { id: "restitue", label: "Restitué", color: "#2DD4BF", bgColor: "#D3FFFC", borderColor: "#B0FFF9" },
];

// Workers are fetched from API but we keep a local cache
let _cachedWorkers: WorkshopWorker[] = [];
export function getCachedWorkers(): WorkshopWorker[] {
  return _cachedWorkers;
}

let _nextId = 100;
function genId() {
  return `gen_${_nextId++}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface AtelierState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  activeSpecialty: "all" | WorkshopSpecialty;
  selectedWeekWorkerId: string;
  jobs: WorkshopJob[];
  charges: WorkCharge[];
  workers: WorkshopWorker[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  hoveredChargeId: string | null;
  carWindows: Record<string, CarWindow>;
  _fetchedOrgId: string | undefined;

  fetchData: (orgId: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setActiveSpecialty: (s: "all" | WorkshopSpecialty) => void;
  setSelectedWeekWorkerId: (id: string) => void;
  setSearchQuery: (query: string) => void;
  setHoveredChargeId: (id: string | null) => void;
  updateJob: (id: string, updates: Partial<Pick<WorkshopJob, "startHour" | "durationHours" | "workerId" | "date">>) => void;
  getFilteredJobs: () => WorkshopJob[];
  getVisibleWorkers: () => WorkshopWorker[];
  placeChargeAsJob: (chargeId: string, workerId: string, startHour: number, durationHours: number, date?: string) => void;
  splitCharge: (chargeId: string, keepHours: number) => void;
  returnJobToCharge: (jobId: string) => void;
  moveChargeToStatus: (chargeId: string, newStatus: WorkCharge["status"]) => void;
}

export const useAtelierStore = create<AtelierState>((set, get) => ({
  selectedDate: todayIso(),
  viewMode: "jour",
  activeSpecialty: "all",
  selectedWeekWorkerId: "",
  jobs: [],
  charges: [],
  workers: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: "",
  hoveredChargeId: null,
  carWindows: {},
  _fetchedOrgId: undefined,

  fetchData: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const showLoading = state.jobs.length === 0 && state.charges.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const [jobs, charges, apiWorkers] = await Promise.all([
        atelierApi.listJobs({ org_id: orgId }),
        atelierApi.listCharges({ org_id: orgId }),
        atelierApi.listWorkers({ org_id: orgId }),
      ]);

      // Derive workers from org members with workshop roles
      const orgMembers = useDataStore.getState().orgMembers;
      const memberWorkers = orgMembers
        .filter((m) => m.status !== "inactive" && m.role in WORKSHOP_ROLE_TO_SPECIALTY)
        .map(memberToWorker)
        .filter((w): w is WorkshopWorker => w !== null);

      // Merge: org-member-derived workers first, then API workers not already covered
      const memberIds = new Set(memberWorkers.map((w) => w.id));
      const extraApiWorkers = apiWorkers.filter((w) => !memberIds.has(w.id));
      const workers = [...memberWorkers, ...extraApiWorkers];

      _cachedWorkers = workers;
      // Build persistent carWindows from both jobs and charges (never remove, only add/update)
      const prevCarWindows = get().carWindows;
      const newCarWindows = { ...prevCarWindows };
      for (const j of jobs) {
        if (j.ordre_id && j.carWindow) {
          newCarWindows[j.ordre_id] = j.carWindow;
        }
      }
      for (const c of charges) {
        if (c.ordre_id && c.carWindow) {
          newCarWindows[c.ordre_id] = c.carWindow;
        }
      }
      set({ jobs, charges, workers, carWindows: newCarWindows, isLoading: false, isRefreshing: false });
    } catch (error) {
      console.error("Error fetching atelier data:", error);
      set({ jobs: [], charges: [], workers: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined });
    }
  },

  setSelectedDate: (date) => set({ selectedDate: date }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setActiveSpecialty: (s) => set({ activeSpecialty: s }),
  setSelectedWeekWorkerId: (id) => set({ selectedWeekWorkerId: id }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setHoveredChargeId: (id) => set({ hoveredChargeId: id }),

  updateJob: (id, updates) => {
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...updates } : j)),
    }));
    atelierApi.updateJob(id, updates as Partial<WorkshopJob>).catch((e) => console.error("Error updating job:", e));
  },

  placeChargeAsJob: (chargeId, workerId, startHour, durationHours, date) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      const worker = state.workers.find((w) => w.id === workerId);
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const jobDate = date ?? state.selectedDate;
      const tempId = genId();
      const newJob: WorkshopJob = {
        id: tempId,
        clientName: charge.clientName,
        plate: "",
        hours: durationHours,
        workerId,
        date: jobDate,
        startHour,
        durationHours,
        status: "en_cours",
        specialty: worker?.specialty ?? charge.specialty,
        ordre_id: charge.ordre_id,
        org_id: orgId,
      };
      const remainingHours = +(charge.hours - durationHours).toFixed(2);
      const newCharges = remainingHours > 0
        ? state.charges.map((c) => (c.id === chargeId ? { ...c, hours: remainingHours } : c))
        : state.charges.filter((c) => c.id !== chargeId);

      // Persist to API
      atelierApi.createJob({ ...newJob, org_id: orgId } as Omit<WorkshopJob, "id"> & { org_id: string }).then((created) => {
        // Replace temp id with real id
        useAtelierStore.setState((s) => ({
          jobs: s.jobs.map((j) => (j.id === tempId ? created : j)),
        }));
      }).catch((e) => console.error("Error creating job:", e));

      if (remainingHours > 0) {
        atelierApi.updateCharge(chargeId, { hours: remainingHours }).catch((e) => console.error("Error updating charge:", e));
      } else {
        atelierApi.deleteCharge(chargeId).catch((e) => console.error("Error deleting charge:", e));
      }

      // Persist carWindow before charge is potentially deleted
      const updatedCarWindows = charge.ordre_id && charge.carWindow
        ? { ...state.carWindows, [charge.ordre_id]: charge.carWindow }
        : state.carWindows;
      return { jobs: [...state.jobs, newJob], charges: newCharges, carWindows: updatedCarWindows };
    }),

  splitCharge: (chargeId, keepHours) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      const remainder = +(charge.hours - keepHours).toFixed(2);
      if (remainder <= 0 || keepHours <= 0) return state;
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: charge.clientName,
        hours: remainder,
        status: charge.status,
        specialty: charge.specialty,
        ordre_id: charge.ordre_id,
        org_id: orgId,
        carWindow: charge.carWindow,
      };

      // Persist: update original charge hours, create new charge for remainder
      atelierApi.updateCharge(chargeId, { hours: keepHours }).catch((e) => console.error("Error updating charge:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? created : c)),
        }));
      }).catch((e) => console.error("Error creating split charge:", e));

      return {
        charges: [
          ...state.charges.map((c) => (c.id === chargeId ? { ...c, hours: keepHours } : c)),
          newCharge,
        ],
      };
    }),

  returnJobToCharge: (jobId) =>
    set((state) => {
      const job = state.jobs.find((j) => j.id === jobId);
      if (!job) return state;
      const orgId = job.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      // Carry carWindow from sibling charge or persistent store
      const siblingCarWindow = job.ordre_id
        ? (state.charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
          || state.carWindows[job.ordre_id])
        : undefined;
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: job.clientName,
        hours: job.durationHours,
        status: "a_repartir",
        specialty: job.specialty,
        ordre_id: job.ordre_id,
        org_id: orgId,
        carWindow: siblingCarWindow,
      };
      atelierApi.deleteJob(jobId).catch((e) => console.error("Error deleting job:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? created : c)),
        }));
      }).catch((e) => console.error("Error creating charge:", e));
      return {
        jobs: state.jobs.filter((j) => j.id !== jobId),
        charges: [...state.charges, newCharge],
      };
    }),

  moveChargeToStatus: (chargeId, newStatus) => {
    set((state) => ({
      charges: state.charges.map((c) => (c.id === chargeId ? { ...c, status: newStatus } : c)),
    }));
    atelierApi.updateCharge(chargeId, { status: newStatus }).catch((e) => console.error("Error updating charge:", e));
  },

  getFilteredJobs: () => {
    const state = get();
    return state.jobs.filter(
      (j) =>
        j.date === state.selectedDate &&
        (state.activeSpecialty === "all" || j.specialty === state.activeSpecialty) &&
        (state.searchQuery === "" ||
          j.clientName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
          j.plate.toLowerCase().includes(state.searchQuery.toLowerCase()))
    );
  },
  getVisibleWorkers: () => {
    const state = get();
    if (state.activeSpecialty === "all") return state.workers;
    return state.workers.filter((w) => w.specialty === state.activeSpecialty);
  },
}));
